// Starter templates offered when creating a new form. Picking one pre-fills
// the editor (fields + theme); the user still edits and saves through the
// normal create flow. Keys mirror the shapes FormFieldsEditor/FormThemeEditor
// already read and write (field.key/type/label/required/placeholder/options,
// theme.primary_color/button_color/...).
//
// `icon` is a `i-lucide-*` class name, `color` drives the picker card accent
// and doubles as the template's theme.primary_color/button_color so the
// gallery preview matches what gets applied.
//
// Onda 2 do módulo (20/09/2026): um modelo passou a carregar mais do que
// campos e tema. Ele pode trazer `scoring` (faixas + pesos por opção),
// `success_message`, `ai_nurture.message_content` e campos com `visible_if`.
// O motivo é medido: a lógica condicional e a pontuação existem completas
// desde a fase 1 e, em setembro/2026, NENHUM dos 17 formulários de produção
// usava qualquer uma das duas. Ninguém monta isso do zero — então os modelos
// de segmento já vêm com as duas montadas e funcionando.
//
// `category` separa os modelos genéricos ('general') dos de segmento
// ('segment') na galeria.

const field = overrides => ({
  required: false,
  placeholder: '',
  options: [],
  group_with_previous: false,
  ...overrides,
});

const baseTheme = color => ({
  primary_color: color,
  button_color: color,
  button_text_color: '#FFFFFF',
  background_color: '#F5F4EF',
  text_color: '#1B1B18',
  logo_url: '',
  header_image_url: '',
  radius: 16,
  font: 'sans',
  // New forms default to the steps (Typeform-style) layout — see
  // FormEditor.vue's defaultTheme(). Set explicitly here too so every
  // starter template is self-contained regardless of that default.
  layout: 'steps',
});

export const FORM_TEMPLATES = [
  {
    id: 'contact',
    category: 'general',
    name: 'Contato',
    description: 'Formulário simples de contato com mensagem livre.',
    icon: 'i-lucide-mail',
    color: '#146B57',
    fields: [
      field({ key: 'nome', type: 'text', label: 'Nome', required: true }),
      field({ key: 'email', type: 'email', label: 'Email', required: true }),
      field({ key: 'telefone', type: 'phone', label: 'Telefone' }),
      field({
        key: 'mensagem',
        type: 'textarea',
        label: 'Mensagem',
        required: true,
        placeholder: 'Como podemos ajudar?',
      }),
    ],
    theme: baseTheme('#146B57'),
  },
  {
    id: 'quote',
    category: 'general',
    name: 'Orçamento',
    description: 'Capture o serviço de interesse e os detalhes do pedido.',
    icon: 'i-lucide-calculator',
    color: '#1D4ED8',
    fields: [
      field({ key: 'nome', type: 'text', label: 'Nome', required: true }),
      field({
        key: 'telefone',
        type: 'phone',
        label: 'Telefone',
        required: true,
      }),
      field({ key: 'empresa', type: 'text', label: 'Empresa' }),
      field({
        key: 'servico',
        type: 'select',
        label: 'Serviço de interesse',
        required: true,
        options: ['Consultoria', 'Implantação', 'Suporte', 'Outro'],
      }),
      field({
        key: 'detalhes',
        type: 'textarea',
        label: 'Detalhes do orçamento',
        placeholder: 'Descreva o que você precisa',
      }),
    ],
    theme: baseTheme('#1D4ED8'),
  },
  {
    id: 'newsletter',
    category: 'general',
    name: 'Newsletter',
    description: 'Captação enxuta com nome, email e consentimento.',
    icon: 'i-lucide-mail-plus',
    color: '#7C3AED',
    fields: [
      field({ key: 'nome', type: 'text', label: 'Nome', required: true }),
      field({ key: 'email', type: 'email', label: 'Email', required: true }),
      field({
        key: 'consentimento',
        type: 'consent',
        label: 'Concordo em receber novidades por email.',
        required: true,
      }),
    ],
    theme: baseTheme('#7C3AED'),
  },
  {
    id: 'lead',
    category: 'general',
    name: 'Cadastro de lead',
    description: 'Qualifique o interesse do lead logo na entrada.',
    icon: 'i-lucide-user-plus',
    color: '#C2410C',
    fields: [
      field({ key: 'nome', type: 'text', label: 'Nome', required: true }),
      field({
        key: 'telefone',
        type: 'phone',
        label: 'Telefone',
        required: true,
      }),
      field({ key: 'email', type: 'email', label: 'Email' }),
      field({
        key: 'interesse',
        type: 'select',
        label: 'Interesse principal',
        options: ['Produto', 'Preço', 'Parceria', 'Outro'],
      }),
    ],
    theme: baseTheme('#C2410C'),
  },
  {
    id: 'scheduling',
    category: 'general',
    name: 'Agendamento',
    description: 'Reúna data e período preferido para o contato retornar.',
    icon: 'i-lucide-calendar-clock',
    color: '#4338CA',
    fields: [
      field({ key: 'nome', type: 'text', label: 'Nome', required: true }),
      field({
        key: 'telefone',
        type: 'phone',
        label: 'Telefone',
        required: true,
      }),
      field({
        key: 'data',
        type: 'date',
        label: 'Data preferida',
        required: true,
      }),
      field({
        key: 'periodo',
        type: 'select',
        label: 'Período',
        options: ['Manhã', 'Tarde', 'Noite'],
      }),
    ],
    theme: baseTheme('#4338CA'),
  },
  // ——— Modelos por segmento ———
  // Cada um chega com a lógica condicional e a pontuação já montadas: é o que
  // o cliente nunca configura partindo do zero.
  {
    id: 'beauty',
    category: 'segment',
    name: 'Salão e barbearia',
    description:
      'Serviço desejado, histórico de química e melhor horário. Pergunta a química só para quem pede progressiva.',
    icon: 'i-lucide-scissors',
    color: '#BE185D',
    fields: [
      field({ key: 'nome', type: 'text', label: 'Seu nome', required: true }),
      field({
        key: 'telefone',
        type: 'phone',
        label: 'WhatsApp',
        required: true,
      }),
      field({
        key: 'servico',
        type: 'select',
        label: 'O que você quer fazer?',
        required: true,
        options: [
          'Corte',
          'Coloração',
          'Escova progressiva',
          'Manicure e pedicure',
          'Maquiagem',
          'Outro',
        ],
        option_points: {
          Corte: 5,
          Coloração: 8,
          'Escova progressiva': 10,
          'Manicure e pedicure': 4,
          Maquiagem: 6,
          Outro: 2,
        },
      }),
      field({
        key: 'quimica',
        type: 'radio',
        label: 'Já fez química no cabelo?',
        options: ['Nunca fiz', 'Já fiz alisamento', 'Já fiz progressiva'],
        visible_if: {
          field: 'servico',
          operator: 'equals',
          value: 'Escova progressiva',
        },
      }),
      field({
        key: 'cliente',
        type: 'radio',
        label: 'Você já é cliente da casa?',
        options: ['Sou cliente', 'Primeira vez'],
        option_points: { 'Sou cliente': 6, 'Primeira vez': 4 },
      }),
      field({
        key: 'horario',
        type: 'select',
        label: 'Melhor horário',
        options: ['Manhã', 'Tarde', 'Noite', 'Sábado'],
      }),
    ],
    scoring: { enabled: true },
    success_message:
      'Recebemos o seu pedido. Já vamos te chamar no WhatsApp com os horários livres.',
    ai_nurture: {
      message_content:
        'Oi, {{nome}}! Vi aqui que você quer {{servico}}. Me diz qual dia te atende melhor que eu já reservo.',
    },
    theme: baseTheme('#BE185D'),
  },
  {
    id: 'clinic',
    category: 'segment',
    name: 'Clínica e consultório',
    description:
      'Especialidade, convênio e urgência. Só pede o nome do plano de quem tem convênio.',
    icon: 'i-lucide-stethoscope',
    color: '#0E7490',
    fields: [
      field({
        key: 'nome',
        type: 'text',
        label: 'Nome completo',
        required: true,
      }),
      field({
        key: 'telefone',
        type: 'phone',
        label: 'WhatsApp',
        required: true,
      }),
      field({
        key: 'especialidade',
        type: 'select',
        label: 'Qual atendimento você procura?',
        required: true,
        options: [
          'Consulta',
          'Retorno',
          'Exame',
          'Procedimento estético',
          'Ainda não sei',
        ],
        option_points: {
          Consulta: 8,
          Retorno: 6,
          Exame: 7,
          'Procedimento estético': 10,
          'Ainda não sei': 3,
        },
      }),
      field({
        key: 'convenio',
        type: 'radio',
        label: 'Vai usar convênio?',
        required: true,
        options: ['Sim', 'Não, particular'],
        option_points: { Sim: 4, 'Não, particular': 8 },
      }),
      field({
        key: 'plano',
        type: 'text',
        label: 'Qual plano?',
        visible_if: { field: 'convenio', operator: 'equals', value: 'Sim' },
      }),
      field({
        key: 'urgencia',
        type: 'select',
        label: 'Para quando?',
        options: ['Esta semana', 'Este mês', 'Só pesquisando'],
        option_points: {
          'Esta semana': 10,
          'Este mês': 6,
          'Só pesquisando': 2,
        },
      }),
    ],
    scoring: { enabled: true },
    success_message:
      'Recebemos os seus dados. Nossa equipe entra em contato para confirmar o horário.',
    ai_nurture: {
      message_content:
        'Olá, {{nome}}! Recebemos seu pedido de {{especialidade}}. Posso te passar os horários disponíveis?',
    },
    theme: baseTheme('#0E7490'),
  },
  {
    id: 'realestate',
    category: 'segment',
    name: 'Imobiliária',
    description:
      'Comprar ou alugar muda as perguntas seguintes: faixa de valor, forma de pagamento e prazo.',
    icon: 'i-lucide-home',
    color: '#B45309',
    fields: [
      field({ key: 'nome', type: 'text', label: 'Seu nome', required: true }),
      field({
        key: 'telefone',
        type: 'phone',
        label: 'WhatsApp',
        required: true,
      }),
      field({
        key: 'intencao',
        type: 'radio',
        label: 'Você quer comprar ou alugar?',
        required: true,
        options: ['Comprar', 'Alugar'],
        option_points: { Comprar: 10, Alugar: 6 },
      }),
      field({
        key: 'tipo',
        type: 'select',
        label: 'Tipo de imóvel',
        options: ['Apartamento', 'Casa', 'Terreno', 'Sala comercial'],
      }),
      field({
        key: 'faixa_compra',
        type: 'select',
        label: 'Faixa de investimento',
        options: [
          'Até R$ 300 mil',
          'De R$ 300 a 600 mil',
          'Acima de R$ 600 mil',
        ],
        option_points: {
          'Até R$ 300 mil': 5,
          'De R$ 300 a 600 mil': 8,
          'Acima de R$ 600 mil': 10,
        },
        visible_if: {
          field: 'intencao',
          operator: 'equals',
          value: 'Comprar',
        },
      }),
      field({
        key: 'pagamento',
        type: 'radio',
        label: 'Como pretende pagar?',
        options: ['À vista', 'Financiado', 'Ainda não sei'],
        option_points: { 'À vista': 10, Financiado: 7, 'Ainda não sei': 3 },
        visible_if: {
          field: 'intencao',
          operator: 'equals',
          value: 'Comprar',
        },
      }),
      field({
        key: 'faixa_aluguel',
        type: 'select',
        label: 'Valor de aluguel que cabe no seu orçamento',
        options: ['Até R$ 1.500', 'De R$ 1.500 a 3.000', 'Acima de R$ 3.000'],
        option_points: {
          'Até R$ 1.500': 5,
          'De R$ 1.500 a 3.000': 8,
          'Acima de R$ 3.000': 10,
        },
        visible_if: { field: 'intencao', operator: 'equals', value: 'Alugar' },
      }),
      field({
        key: 'prazo',
        type: 'select',
        label: 'Para quando?',
        options: ['Neste mês', 'Em até 3 meses', 'Sem pressa'],
        option_points: {
          'Neste mês': 10,
          'Em até 3 meses': 6,
          'Sem pressa': 2,
        },
      }),
    ],
    scoring: { enabled: true },
    success_message:
      'Obrigado! Um corretor vai te mandar as opções que batem com o que você procura.',
    ai_nurture: {
      message_content:
        'Oi, {{nome}}! Separei alguns imóveis para {{intencao}}. Posso te mandar agora?',
    },
    theme: baseTheme('#B45309'),
  },
  {
    id: 'law',
    category: 'segment',
    name: 'Advocacia',
    description:
      'Área do caso, prazo e resumo. Pede o número do processo só de quem já tem ação em andamento.',
    icon: 'i-lucide-scale',
    color: '#334155',
    fields: [
      field({
        key: 'nome',
        type: 'text',
        label: 'Nome completo',
        required: true,
      }),
      field({
        key: 'telefone',
        type: 'phone',
        label: 'WhatsApp',
        required: true,
      }),
      field({
        key: 'area',
        type: 'select',
        label: 'Qual a área do seu caso?',
        required: true,
        options: [
          'Trabalhista',
          'Família',
          'Previdenciário',
          'Cível',
          'Empresarial',
          'Criminal',
        ],
      }),
      field({
        key: 'processo',
        type: 'radio',
        label: 'Já existe processo em andamento?',
        required: true,
        options: ['Sim', 'Não'],
        option_points: { Sim: 6, Não: 8 },
      }),
      field({
        key: 'numero_processo',
        type: 'text',
        label: 'Número do processo',
        visible_if: { field: 'processo', operator: 'equals', value: 'Sim' },
      }),
      field({
        key: 'urgencia',
        type: 'select',
        label: 'Qual a urgência?',
        options: [
          'Tenho prazo correndo',
          'Nas próximas semanas',
          'Só quero uma orientação',
        ],
        option_points: {
          'Tenho prazo correndo': 10,
          'Nas próximas semanas': 6,
          'Só quero uma orientação': 3,
        },
      }),
      field({
        key: 'resumo',
        type: 'textarea',
        label: 'Conte em poucas linhas o que aconteceu',
        required: true,
        placeholder: 'O que aconteceu, quando, e o que você espera resolver.',
        answer_points: 6,
      }),
    ],
    scoring: { enabled: true },
    success_message:
      'Recebemos o seu relato. Um advogado do escritório vai analisar e responder.',
    ai_nurture: {
      message_content:
        'Olá, {{nome}}. Recebi o seu relato sobre {{area}}. Posso te fazer duas perguntas rápidas para o advogado já chegar com o caso na mão?',
    },
    theme: baseTheme('#334155'),
  },
  {
    id: 'fitness',
    category: 'segment',
    name: 'Academia e personal',
    description:
      'Objetivo, experiência e plano de interesse. Pergunta a restrição médica só para quem tem.',
    icon: 'i-lucide-dumbbell',
    color: '#15803D',
    fields: [
      field({ key: 'nome', type: 'text', label: 'Seu nome', required: true }),
      field({
        key: 'telefone',
        type: 'phone',
        label: 'WhatsApp',
        required: true,
      }),
      field({
        key: 'objetivo',
        type: 'select',
        label: 'Qual o seu objetivo?',
        required: true,
        options: [
          'Emagrecer',
          'Ganhar massa',
          'Condicionamento',
          'Saúde e bem-estar',
        ],
      }),
      field({
        key: 'experiencia',
        type: 'radio',
        label: 'Como está a sua rotina hoje?',
        options: ['Nunca treinei', 'Já treinei e parei', 'Treino atualmente'],
        option_points: {
          'Nunca treinei': 6,
          'Já treinei e parei': 8,
          'Treino atualmente': 10,
        },
      }),
      field({
        key: 'plano',
        type: 'select',
        label: 'Plano de interesse',
        options: ['Mensal', 'Trimestral', 'Anual'],
        option_points: { Mensal: 5, Trimestral: 8, Anual: 10 },
      }),
      field({
        key: 'restricao',
        type: 'radio',
        label: 'Tem alguma restrição médica?',
        options: ['Sim', 'Não'],
      }),
      field({
        key: 'qual_restricao',
        type: 'text',
        label: 'Qual restrição?',
        visible_if: { field: 'restricao', operator: 'equals', value: 'Sim' },
      }),
    ],
    scoring: { enabled: true },
    success_message:
      'Show! Vamos te chamar no WhatsApp para marcar a sua aula experimental.',
    ai_nurture: {
      message_content:
        'Oi, {{nome}}! Seu objetivo é {{objetivo}}, certo? Quer marcar uma aula experimental essa semana?',
    },
    theme: baseTheme('#15803D'),
  },
  {
    id: 'construction',
    category: 'segment',
    name: 'Construtora e lançamento',
    description:
      'Morar ou investir, entrada e financiamento aprovado. Separa o lead pronto de quem está começando a pesquisar.',
    icon: 'i-lucide-building-2',
    color: '#7C3AED',
    fields: [
      field({
        key: 'nome',
        type: 'text',
        label: 'Nome completo',
        required: true,
      }),
      field({
        key: 'telefone',
        type: 'phone',
        label: 'WhatsApp',
        required: true,
      }),
      field({ key: 'email', type: 'email', label: 'E-mail' }),
      field({
        key: 'finalidade',
        type: 'radio',
        label: 'É para morar ou investir?',
        required: true,
        options: ['Morar', 'Investir'],
        option_points: { Morar: 8, Investir: 10 },
      }),
      field({
        key: 'entrada',
        type: 'select',
        label: 'Quanto você tem de entrada?',
        options: [
          'Até R$ 30 mil',
          'De R$ 30 a 80 mil',
          'Acima de R$ 80 mil',
          'Ainda estou juntando',
        ],
        option_points: {
          'Até R$ 30 mil': 5,
          'De R$ 30 a 80 mil': 8,
          'Acima de R$ 80 mil': 10,
          'Ainda estou juntando': 2,
        },
      }),
      field({
        key: 'financiamento',
        type: 'radio',
        label: 'Já tem financiamento aprovado?',
        options: ['Sim, aprovado', 'Em análise', 'Ainda não'],
        option_points: { 'Sim, aprovado': 10, 'Em análise': 7, 'Ainda não': 3 },
      }),
      field({
        key: 'banco',
        type: 'text',
        label: 'Em qual banco?',
        visible_if: {
          field: 'financiamento',
          operator: 'equals',
          value: 'Sim, aprovado',
        },
      }),
      field({
        key: 'prazo',
        type: 'select',
        label: 'Quando pretende fechar?',
        options: ['Neste mês', 'Em até 6 meses', 'Só pesquisando'],
        option_points: {
          'Neste mês': 10,
          'Em até 6 meses': 6,
          'Só pesquisando': 2,
        },
      }),
    ],
    scoring: { enabled: true },
    success_message:
      'Recebemos o seu interesse. Um consultor vai te mandar a tabela e as condições.',
    ai_nurture: {
      message_content:
        'Oi, {{nome}}! Vi que você quer {{finalidade}}. Posso te mandar a tabela de valores e as unidades disponíveis?',
    },
    theme: baseTheme('#7C3AED'),
  },
];

// Ordem das abas da galeria. 'general' primeiro porque é o que serve para
// qualquer conta; 'segment' é o diferencial e fica logo abaixo, visível sem
// precisar procurar.
export const TEMPLATE_CATEGORIES = ['general', 'segment'];

// Um modelo sem `category` conta como genérico — evita sumir da galeria caso
// alguém acrescente um modelo e esqueça o campo.
export const templatesByCategory = category =>
  FORM_TEMPLATES.filter(
    template => (template.category || 'general') === category
  );

export const getFormTemplateById = id =>
  FORM_TEMPLATES.find(template => template.id === id) || null;
